// Phones: swipe a bottom sheet down to close it — one document-level gesture
// recogniser per app (Backoffice + B2C), so no sheet has to wire its own
// touch handlers. Each app passes the list of sheets it knows (SheetSwipeSpec:
// how to find the panel, whether it may be dismissed, and how to close it
// through the sheet's OWN close path — close button / mask / Esc — so the
// app state stays the single source of truth).
//
// The drag starts
//   • from the sheet's top area (grabber / title bar: the first 64px of the
//     panel, a sticky header, .ant-modal-header / .ant-drawer-header or any
//     [data-sheet-handle]) — always;
//   • anywhere on the sheet when nothing under the finger is scrolled (every
//     scroll container between the finger and the panel is at its top) and
//     the finger moves down — unless the spec is handle-only.
// It never starts on form fields, sliders, maps, iframes, video, canvas or
// inside [data-sheet-no-swipe]; a mostly-horizontal move (carousels, tabs)
// or an upward one (scrolling the content) hands the gesture back.
// The panel follows the finger (translateY), the backdrop fades; on release
// it closes when dragged past 25% of its height or flicked down, otherwise it
// springs back. Width-based: never on ≥ 768px.

export interface SheetSwipeSpec {
  /** Selector (matched with closest() from the touch target) of the panel that moves. */
  panel: string;
  /** Extra check on the matched panel (e.g. only pop-confirms among popovers). */
  accept?: (panel: HTMLElement) => boolean;
  /** The dimmed layer behind the sheet — faded while dragging. */
  backdrop?: (panel: HTMLElement) => HTMLElement | null | undefined;
  /** false → a blocking sheet (required choice / no close affordance): never dragged. */
  canDismiss?: (panel: HTMLElement) => boolean;
  /** Closes the sheet the way its own UI would. */
  dismiss: (panel: HTMLElement) => void;
  /** Drag only from the top area / header, never by pulling the content (chats). */
  handleOnly?: boolean | ((panel: HTMLElement) => boolean);
  /** Skip the "docked to the bottom edge, full width" geometry check. */
  skipGeometryCheck?: boolean;
}

const PHONE_MEDIA_QUERY = '(max-width: 767px)';
// top strip of the panel that always drags the sheet (grabber + title bar)
const HANDLE_ZONE = 64;
// movement before the gesture picks a direction
const SLOP = 6;
const DISMISS_RATIO = 0.25;
const FLICK_VELOCITY = 0.5; // px/ms
const FLICK_MIN_DISTANCE = 24;
const OUT_DURATION = 200;
const BACK_DURATION = 260;
const CLOSE_WAIT = 1200;
const EASING = 'cubic-bezier(0.2, 0.8, 0.2, 1)';

const NO_SWIPE_TARGETS = [
  'input',
  'textarea',
  'select',
  '[contenteditable=""]',
  '[contenteditable="true"]',
  'iframe',
  'video',
  'canvas',
  '.ant-slider',
  '.leaflet-container',
  '.gm-style',
  '.mapboxgl-map',
  '.maplibregl-map',
  '[data-sheet-no-swipe]',
].join(',');

const HANDLE_TARGETS = '[data-sheet-handle], .ant-modal-header, .ant-drawer-header';

export const isPhoneViewport = () =>
  typeof window !== 'undefined' &&
  !!window.matchMedia &&
  window.matchMedia(PHONE_MEDIA_QUERY).matches;

const isHidden = (el: HTMLElement) =>
  !el.isConnected ||
  el.getClientRects().length === 0 ||
  window.getComputedStyle(el).visibility === 'hidden';

// Docked to the bottom of what is visible (above the keyboard when a sheet
// follows it) and edge to edge — a centred dialog never qualifies.
const isBottomSheet = (panel: HTMLElement) => {
  const rect = panel.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return false;
  const viewport = window.visualViewport;
  const visibleBottom = viewport
    ? Math.min(window.innerHeight, viewport.offsetTop + viewport.height)
    : window.innerHeight;
  return (
    rect.bottom >= visibleBottom - 24 &&
    rect.bottom <= window.innerHeight + 2 &&
    rect.width >= document.documentElement.clientWidth - 2
  );
};

const isScrollable = (el: HTMLElement) => {
  if (el.scrollHeight <= el.clientHeight + 1) return false;
  const { overflowY } = window.getComputedStyle(el);
  return overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';
};

// Between the finger and the panel (inclusive): is any scroll container away
// from its top, and is the finger on something pinned (a sticky title bar)?
const inspectPath = (target: HTMLElement, panel: HTMLElement) => {
  let scrolled = false;
  let pinned = false;
  let node: HTMLElement | null = target;
  while (node) {
    if (node.scrollTop > 0 && isScrollable(node)) scrolled = true;
    if (node !== panel) {
      const { position } = window.getComputedStyle(node);
      if (position === 'sticky' || position === 'fixed') pinned = true;
    }
    if (node === panel) break;
    node = node.parentElement;
  }
  return { scrolled, pinned };
};

interface SavedStyle {
  value: string;
  priority: string;
}

const saveStyle = (el: HTMLElement, props: string[]) =>
  props.reduce<Record<string, SavedStyle>>((acc, prop) => {
    acc[prop] = {
      value: el.style.getPropertyValue(prop),
      priority: el.style.getPropertyPriority(prop),
    };
    return acc;
  }, {});

const restoreStyle = (el: HTMLElement, saved: Record<string, SavedStyle>) => {
  Object.keys(saved).forEach((prop) => {
    const { value, priority } = saved[prop];
    if (value) el.style.setProperty(prop, value, priority);
    else el.style.removeProperty(prop);
  });
};

// inline !important: beats the sheets' own `transform: none !important` and
// their enter / leave keyframes
const setImportant = (el: HTMLElement, prop: string, value: string) =>
  el.style.setProperty(prop, value, 'important');

interface Gesture {
  spec: SheetSwipeSpec;
  panel: HTMLElement;
  startX: number;
  startY: number;
  fromHandle: boolean;
  dragging: boolean;
  offset: number;
  height: number;
  baseTransform: string;
  backdrop: HTMLElement | null;
  backdropOpacity: number;
  samples: { y: number; t: number }[];
  panelStyle?: Record<string, SavedStyle>;
  backdropStyle?: Record<string, SavedStyle>;
}

const findSheet = (target: HTMLElement, specs: SheetSwipeSpec[]) => {
  let best: { spec: SheetSwipeSpec; panel: HTMLElement } | null = null;
  specs.forEach((spec) => {
    const panel = target.closest<HTMLElement>(spec.panel);
    if (!panel || (spec.accept && !spec.accept(panel))) return;
    // the innermost sheet wins (a popup rendered inside a modal sheet)
    if (!best || best.panel.contains(panel)) best = { spec, panel };
  });
  return best as { spec: SheetSwipeSpec; panel: HTMLElement } | null;
};

/** Installs the recogniser; returns the uninstall function. */
export const installSheetSwipe = (specs: SheetSwipeSpec[]) => {
  if (typeof document === 'undefined') return () => undefined;
  let gesture: Gesture | null = null;
  // panels finishing a close / spring-back animation
  const busy = new WeakSet<HTMLElement>();

  const applyOffset = (g: Gesture, offset: number) => {
    g.offset = offset;
    setImportant(
      g.panel,
      'transform',
      `translate3d(0, ${offset}px, 0)${g.baseTransform !== 'none' ? ` ${g.baseTransform}` : ''}`
    );
    if (g.backdrop) {
      const progress = Math.min(1, offset / Math.max(1, g.height));
      setImportant(g.backdrop, 'opacity', String(g.backdropOpacity * (1 - progress)));
    }
  };

  const restore = (g: Gesture) => {
    if (g.panelStyle) restoreStyle(g.panel, g.panelStyle);
    if (g.backdrop && g.backdropStyle) restoreStyle(g.backdrop, g.backdropStyle);
    busy.delete(g.panel);
  };

  const springBack = (g: Gesture) => {
    const transition = `transform ${BACK_DURATION}ms ${EASING}, opacity ${BACK_DURATION}ms ${EASING}`;
    setImportant(g.panel, 'transition', transition);
    if (g.backdrop) setImportant(g.backdrop, 'transition', transition);
    applyOffset(g, 0);
    window.setTimeout(() => restore(g), BACK_DURATION + 20);
  };

  const close = (g: Gesture) => {
    const transition = `transform ${OUT_DURATION}ms ${EASING}, opacity ${OUT_DURATION}ms ${EASING}`;
    setImportant(g.panel, 'transition', transition);
    if (g.backdrop) setImportant(g.backdrop, 'transition', transition);
    applyOffset(g, g.height + 24);
    window.setTimeout(() => {
      try {
        g.spec.dismiss(g.panel);
      } catch (error) {
        // a close path that throws must not leave the sheet off-screen
        // eslint-disable-next-line no-console
        console.error(error);
      }
      // keep it out of view through the sheet's own leave animation, then
      // drop the inline styles so it opens normally next time; if it did not
      // close after all, bring it back
      const startedAt = Date.now();
      const check = () => {
        // gone, hidden, or no longer presented as this sheet (a panel that
        // returns to the page flow when its sheet closes)
        if (isHidden(g.panel) || !g.panel.matches(g.spec.panel)) {
          restore(g);
        } else if (Date.now() - startedAt > CLOSE_WAIT) {
          springBack(g);
        } else {
          window.setTimeout(check, 60);
        }
      };
      check();
    }, OUT_DURATION);
  };

  const startDrag = (g: Gesture) => {
    g.dragging = true;
    busy.add(g.panel);
    g.height = g.panel.getBoundingClientRect().height;
    g.panelStyle = saveStyle(g.panel, ['transform', 'transition', 'will-change']);
    const computed = window.getComputedStyle(g.panel).transform;
    // our own leftover / the inline push offset is not the resting transform
    g.baseTransform =
      computed && computed !== 'matrix(1, 0, 0, 1, 0, 0)' && !g.panel.style.transform
        ? computed
        : 'none';
    setImportant(g.panel, 'transition', 'none');
    setImportant(g.panel, 'will-change', 'transform');
    if (g.backdrop) {
      g.backdropStyle = saveStyle(g.backdrop, ['opacity', 'transition']);
      g.backdropOpacity = Number(window.getComputedStyle(g.backdrop).opacity) || 1;
      setImportant(g.backdrop, 'transition', 'none');
    }
  };

  const onTouchStart = (event: TouchEvent) => {
    if (gesture?.dragging) {
      // a second finger: put the sheet back
      springBack(gesture);
      gesture = null;
      return;
    }
    gesture = null;
    if (event.touches.length !== 1 || !isPhoneViewport()) return;
    const target = event.target as HTMLElement | null;
    if (!target?.closest || target.closest(NO_SWIPE_TARGETS)) return;

    const found = findSheet(target, specs);
    if (!found) return;
    const { spec, panel } = found;
    if (busy.has(panel)) return;
    if (!spec.skipGeometryCheck && !isBottomSheet(panel)) return;
    if (spec.canDismiss && !spec.canDismiss(panel)) return;

    const touch = event.touches[0];
    const rect = panel.getBoundingClientRect();
    const { scrolled, pinned } = inspectPath(target, panel);
    const handleTarget = target.closest(HANDLE_TARGETS);
    const fromHandle =
      (!!handleTarget && panel.contains(handleTarget)) ||
      (touch.clientY - rect.top <= HANDLE_ZONE && (!scrolled || pinned));
    const handleOnly =
      typeof spec.handleOnly === 'function' ? spec.handleOnly(panel) : !!spec.handleOnly;
    if (!fromHandle && (handleOnly || scrolled)) return;

    gesture = {
      spec,
      panel,
      startX: touch.clientX,
      startY: touch.clientY,
      fromHandle,
      dragging: false,
      offset: 0,
      height: rect.height,
      baseTransform: 'none',
      backdrop: spec.backdrop?.(panel) ?? null,
      backdropOpacity: 1,
      samples: [{ y: touch.clientY, t: event.timeStamp }],
    };
  };

  const onTouchMove = (event: TouchEvent) => {
    const g = gesture;
    if (!g) return;
    const touch = event.touches[0];
    if (!touch) return;
    const dx = touch.clientX - g.startX;
    const dy = touch.clientY - g.startY;

    if (!g.dragging) {
      if (Math.abs(dx) < SLOP && Math.abs(dy) < SLOP) {
        // a drag on the title bar never scrolls / bounces the page
        if (g.fromHandle && event.cancelable) event.preventDefault();
        return;
      }
      if (Math.abs(dx) > Math.abs(dy) || dy <= 0) {
        gesture = null; // horizontal swipe or scrolling the content up
        return;
      }
      startDrag(g);
    }

    if (event.cancelable) event.preventDefault();
    // a little resistance above the resting position
    applyOffset(g, dy > 0 ? dy : dy / 4);
    g.samples.push({ y: touch.clientY, t: event.timeStamp });
    if (g.samples.length > 8) g.samples.shift();
  };

  const onTouchEnd = (event: TouchEvent) => {
    const g = gesture;
    if (!g) return;
    gesture = null;
    if (!g.dragging) return;
    const last = g.samples[g.samples.length - 1];
    const first =
      g.samples.find((sample) => last.t - sample.t <= 100) ?? g.samples[0];
    const velocity = last.t > first.t ? (last.y - first.y) / (last.t - first.t) : 0;
    const shouldClose =
      event.type === 'touchend' &&
      (g.offset > g.height * DISMISS_RATIO ||
        (velocity > FLICK_VELOCITY && g.offset > FLICK_MIN_DISTANCE));
    if (shouldClose) close(g);
    else springBack(g);
  };

  const onResize = () => {
    if (gesture?.dragging && !isPhoneViewport()) {
      springBack(gesture);
      gesture = null;
    }
  };

  // capture: sheets stop their touch events from bubbling (isolated portals)
  document.addEventListener('touchstart', onTouchStart, { capture: true, passive: true });
  document.addEventListener('touchmove', onTouchMove, { capture: true, passive: false });
  document.addEventListener('touchend', onTouchEnd, { capture: true, passive: true });
  document.addEventListener('touchcancel', onTouchEnd, { capture: true, passive: true });
  window.addEventListener('resize', onResize);

  return () => {
    document.removeEventListener('touchstart', onTouchStart, true);
    document.removeEventListener('touchmove', onTouchMove, true);
    document.removeEventListener('touchend', onTouchEnd, true);
    document.removeEventListener('touchcancel', onTouchEnd, true);
    window.removeEventListener('resize', onResize);
  };
};
